"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  backoffMs,
  createMemoryPersistence,
  createOutboxStore,
  drainable,
  type OpSemantics,
  type OutboxOp,
  type OutboxStore,
  PermanentOpError,
  patchOp,
  pruneExpired,
  removeOp,
  saveOutbox,
  upsertOp,
} from "./outbox-store";

/** What a caller hands to `enqueue`. `clientKey`/`status`/`attempts`/
 *  `createdAt` are filled in by the hook. */
export interface EnqueueInput {
  /** Sender key the `run` dispatcher switches on (e.g. "sendMessage"). */
  op: string;
  semantics: OpSemantics;
  payload: unknown;
  /** For `convergence` ops — supersedes a pending op with the same key. */
  convergenceKey?: string;
  /** For `beacon` ops — how long (ms from now) before the op is stale + dropped
   *  undelivered. Ignored for other semantics. */
  ttlMs?: number;
}

/** The ack a sender resolves with. Messages carry messageId/chatId; other ops
 *  can return `{}`. */
export interface OutboxAck {
  messageId?: string | null;
  chatId?: string | null;
}

/** Helpers handed to `run` for the duration of one attempt. */
export interface RunContext {
  /** Persist a new payload for THIS op mid-attempt — e.g. after uploading a
   *  pending media item, stash its `mediaId` so a later retry (send failed)
   *  doesn't re-upload the bytes. */
  patch: (payload: unknown) => void;
}

interface UseOutboxOpts {
  /** Per-integration queue scope; the hook is inert until this is set. */
  publicKey: string | null;
  enabled: boolean;
  /** Isolated preview session: the queue lives in memory only (no IndexedDB, no cross-tab sync). */
  isolated?: boolean;
  /** Hold every send (BE preview token rejected): ops stay queued, nothing is lost. An op already
   *  in flight finishes; when this goes back to `false` the queue drains at once, backoffs reset. */
  paused?: boolean;
  /** Dispatches ONE op to its BE call (switch on `op.op`). MUST reject on
   *  failure so the op is kept + retried; resolve with the ack on success. */
  run: (op: OutboxOp, ctx: RunContext) => Promise<OutboxAck>;
  /** Fired right before a sent op is dropped — lets the caller reconcile (a
   *  message swaps its optimistic bubble → messageId; other ops may no-op). */
  onSent: (op: OutboxOp, ack: OutboxAck) => void;
  /** Classify a thrown `run` error as PERMANENT — a BE rejection a retry can
   *  only reproduce (input validation / 400). Such an op is DROPPED (removed
   *  from the queue) instead of retried, since it can never succeed (see
   *  `PermanentOpError`). Keeps the outbox generic: the caller owns the
   *  (Apollo-shaped) error knowledge. A `run` may also throw `PermanentOpError`
   *  directly. Absent → every failure is treated transient. */
  isPermanent?: (err: unknown) => boolean;
  /** Fired right after a PERMANENT failure drops an op from the queue — lets
   *  the caller drop the matching UI (a send's optimistic bubble that will
   *  never deliver; `error` says why). Not called for transient failures (those keep retrying). */
  onDropped?: (op: OutboxOp, error: unknown) => void;
}

export interface UseOutbox {
  /** Current queue (queued / sending / failed) for rendering. */
  ops: OutboxOp[];
  /** Live network reachability (drives the offline banner). */
  online: boolean;
  /** Queue an operation and kick the drain. Returns the created op (its
   *  `clientKey` is e.g. the optimistic bubble's id). */
  enqueue: (input: EnqueueInput) => OutboxOp;
  /** Manual retry of a failed op — re-queue + drain now (resets backoff). */
  retry: (clientKey: string) => void;
  /** Discard a queued/failed op (the visitor gave up on it). */
  discard: (clientKey: string) => void;
  /** Forget EVERY queued op, in memory and in storage (shutdown / sign-out on a shared device). */
  clear: () => void;
}

function newClientKey(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `ck-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Module-level (stable, no closure) so it isn't an effect/callback dependency. */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

/**
 * Drives the offline operation queue: holds the persisted ops, exposes enqueue/
 * retry/discard, and runs a sequential FIFO drain that retries on reconnect
 * with exponential backoff.
 *
 * The drain sends ONE op at a time (preserves order + lets a new thread's first
 * message create its chatId before later ops need it). A failed op gets an
 * exponential-backoff `nextAttemptAt`; the loop moves on to any OTHER ready op,
 * then arms a single timer for the soonest still-waiting one and stops.
 * `navigator.onLine` + the `online` event gate/trigger the drain (which also
 * clears backoff so a reconnect retries immediately); a `sending` status
 * excludes an in-flight op from re-drain (the soft-lock against double-send).
 * Stale `beacon` ops are pruned each drain so telemetry never replays late.
 */
export function useOutbox({
  publicKey,
  enabled,
  isolated = false,
  paused = false,
  run,
  onSent,
  isPermanent,
  onDropped,
}: UseOutboxOpts): UseOutbox {
  const [ops, setOps] = useState<OutboxOp[]>([]);
  const [online, setOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  // Latest callbacks without re-subscribing the store / re-arming listeners.
  const runRef = useRef(run);
  const onSentRef = useRef(onSent);
  const isPermanentRef = useRef(isPermanent);
  const onDroppedRef = useRef(onDropped);
  useEffect(() => {
    runRef.current = run;
    onSentRef.current = onSent;
    isPermanentRef.current = isPermanent;
    onDroppedRef.current = onDropped;
  });

  const storeRef = useRef<OutboxStore | null>(null);
  const drainingRef = useRef(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const backoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `drain` reads live state through the store, but the effect below needs a
  // stable reference to call it from listeners — hold it in a ref.
  const drainRef = useRef<() => void>(() => {});

  const drain = useCallback(() => {
    const store = storeRef.current;
    if (!store || drainingRef.current) return;
    if (isOffline() || pausedRef.current) return;
    drainingRef.current = true;
    (async () => {
      try {
        while (true) {
          if (isOffline() || pausedRef.current) break;
          const now = Date.now();
          // Drop stale beacon (telemetry) ops before considering the queue.
          const pruned = pruneExpired(store.get(), now);
          if (pruned !== store.get()) store.set(pruned);
          const pending = drainable(store.get());
          // The first op past its backoff. If none are ready yet, arm ONE timer
          // for the soonest and stop — this serialises retries across every
          // trigger so a failed op isn't attempted twice back-to-back.
          const next = pending.find((e) => (e.nextAttemptAt ?? 0) <= now);
          if (!next) {
            const soonest = pending.reduce(
              (min, e) => Math.min(min, e.nextAttemptAt ?? 0),
              Number.POSITIVE_INFINITY,
            );
            if (soonest !== Number.POSITIVE_INFINITY && soonest > now) {
              if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
              backoffTimerRef.current = setTimeout(() => drainRef.current(), soonest - now);
            }
            break;
          }
          store.set(
            patchOp(store.get(), next.clientKey, { status: "sending", sendingSince: Date.now() }),
          );
          try {
            const ack = await runRef.current(next, {
              // Persist an updated payload mid-attempt (e.g. a just-uploaded
              // media id) so a retry after a later failure doesn't redo it.
              patch: (payload) => store.set(patchOp(store.get(), next.clientKey, { payload })),
            });
            onSentRef.current(next, ack);
            store.set(removeOp(store.get(), next.clientKey));
          } catch (err) {
            // A PERMANENT failure (the BE rejected the input — a retry can only
            // reproduce it, e.g. text over the char cap) can never succeed, so
            // DROP the op from the queue and let the caller drop its UI too.
            // Anything else is transient (offline / 5xx) → back off + retry.
            const permanent =
              err instanceof PermanentOpError || isPermanentRef.current?.(err) === true;
            if (permanent) {
              store.set(removeOp(store.get(), next.clientKey));
              onDroppedRef.current?.(next, err);
            } else {
              const attempts = next.attempts + 1;
              store.set(
                patchOp(store.get(), next.clientKey, {
                  status: "failed",
                  attempts,
                  nextAttemptAt: Date.now() + backoffMs(attempts),
                }),
              );
            }
            // Don't break — loop on to any OTHER ready op; a transient failure
            // now sits behind its backoff, so the loop will schedule + stop once
            // nothing is ready. (One attempt per op per backoff cycle.)
          }
        }
      } finally {
        drainingRef.current = false;
      }
    })();
  }, []);
  drainRef.current = drain;

  // Create the store for this publicKey; subscribe React state to it.
  useEffect(() => {
    if (!enabled || !publicKey) {
      setOps([]);
      return;
    }
    const store = isolated
      ? createOutboxStore(publicKey, createMemoryPersistence(), { crossTab: false })
      : createOutboxStore(publicKey);
    storeRef.current = store;
    setOps(store.get());
    const off = store.subscribe((e) => setOps(e));
    // Drain whatever rehydrated from IDB (ops left un-acked on the last visit).
    void store.ready.then(() => {
      setOps(store.get());
      drainRef.current();
    });
    return () => {
      off();
      store.dispose();
      storeRef.current = null;
    };
  }, [enabled, publicKey, isolated]);

  // Resume after a pause: the ops that failed against the rejected token retry NOW (the same reset a
  // reconnect does), then the queue drains in order.
  const wasPausedRef = useRef(paused);
  useEffect(() => {
    const was = wasPausedRef.current;
    wasPausedRef.current = paused;
    if (paused || !was) return;
    if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
    const store = storeRef.current;
    if (store) {
      const cleared = store.get().map((e) => ({ ...e, nextAttemptAt: 0 }));
      if (cleared.length > 0) store.set(cleared);
    }
    drainRef.current();
  }, [paused]);

  // Reconnect handling: retry immediately when the browser regains network,
  // and keep the banner's `online` flag live.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => {
      setOnline(true);
      if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
      // Reconnect → clear every op's backoff so the queue retries NOW rather
      // than waiting out a timer that was counting down offline. (Permanent
      // failures are already gone — dropped at failure time, not kept.)
      const store = storeRef.current;
      if (store) {
        const cleared = store.get().map((e) => ({ ...e, nextAttemptAt: 0 }));
        if (cleared.length > 0) store.set(cleared);
      }
      drainRef.current();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(
    () => () => {
      if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
    },
    [],
  );

  const enqueue = useCallback((input: EnqueueInput): OutboxOp => {
    const op: OutboxOp = {
      clientKey: newClientKey(),
      op: input.op,
      semantics: input.semantics,
      ...(input.convergenceKey !== undefined ? { convergenceKey: input.convergenceKey } : {}),
      payload: input.payload,
      createdAt: Date.now(),
      status: "queued",
      attempts: 0,
      ...(input.semantics === "beacon" && input.ttlMs !== undefined
        ? { expiresAt: Date.now() + input.ttlMs }
        : {}),
    };
    const store = storeRef.current;
    if (store) {
      store.set(upsertOp(store.get(), op));
      drainRef.current();
    }
    return op;
  }, []);

  const retry = useCallback((clientKey: string) => {
    const store = storeRef.current;
    if (!store) return;
    // Reset backoff so a manual retry attempts immediately.
    if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
    store.set(patchOp(store.get(), clientKey, { status: "queued", attempts: 0, nextAttemptAt: 0 }));
    drainRef.current();
  }, []);

  const discard = useCallback((clientKey: string) => {
    const store = storeRef.current;
    if (!store) return;
    store.set(removeOp(store.get(), clientKey));
  }, []);

  const clear = useCallback(() => {
    if (backoffTimerRef.current) clearTimeout(backoffTimerRef.current);
    const store = storeRef.current;
    if (store) store.set([]);
    else if (publicKey) void saveOutbox(publicKey, []);
  }, [publicKey]);

  return { ops, online, enqueue, retry, discard, clear };
}
